import React, { useState } from 'react';
import { getApiBaseUrl } from '../../config/newgenome-config.js';
import './Api.css';

const Api = ({ cookieId }) => {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [osType, setOsType] = useState('windows');

  const baseUrl = getApiBaseUrl();

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const submitExamples = {
    windows: [
      {
        title: 'Gene Lookup',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/jobs/submit" \`
  -Method POST \`
  -Headers @{ "X-API-Key" = "${cookieId}"; "Content-Type" = "application/json" } \`
  -Body '{"job_type": "gene", "input_text": "BRCA1"}'`
      },
      {
        title: 'Single SNV Variant',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/jobs/submit" \`
  -Method POST \`
  -Headers @{ "X-API-Key" = "${cookieId}"; "Content-Type" = "application/json" } \`
  -Body '{"job_type": "single-variant", "input_text": "chr17:43094464:G:A"}'`
      },
      {
        title: 'Single SNV Variant (With Options)',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/jobs/submit" \`
  -Method POST \`
  -Headers @{ "X-API-Key" = "${cookieId}"; "Content-Type" = "application/json" } \`
  -Body '{"job_type": "single-variant", "input_text": "chr13_32340300_C_T", "genome": "GRCH38", "gender": "Female"}'`
      },
      {
        title: 'CNV/SV - Deletion',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/jobs/submit" \`
  -Method POST \`
  -Headers @{ "X-API-Key" = "${cookieId}"; "Content-Type" = "application/json" } \`
  -Body '{"job_type": "single-sv-cnv", "input_text": "chr7_55000000_55200000_DEL", "genome": "GRCH38"}'`
      },
      {
        title: 'CNV/SV - Duplication',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/jobs/submit" \`
  -Method POST \`
  -Headers @{ "X-API-Key" = "${cookieId}"; "Content-Type" = "application/json" } \`
  -Body '{"job_type": "single-sv-cnv", "input_text": "chr22_20000000_21000000_DUP", "genome": "GRCH38"}'`
      },
      {
        title: 'CNV/SV - Inversion',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/jobs/submit" \`
  -Method POST \`
  -Headers @{ "X-API-Key" = "${cookieId}"; "Content-Type" = "application/json" } \`
  -Body '{"job_type": "single-sv-cnv", "input_text": "chrX_100000_500000_INV", "genome": "GRCH38"}'`
      }
    ],
    linux: [
      {
        title: 'Gene Lookup',
        code: `curl -X POST "${baseUrl}/api/v1/jobs/submit" \\
  -H "X-API-Key: ${cookieId}" \\
  -H "Content-Type: application/json" \\
  -d '{"job_type": "gene", "input_text": "BRCA1"}'`
      },
      {
        title: 'Single SNV Variant',
        code: `curl -X POST "${baseUrl}/api/v1/jobs/submit" \\
  -H "X-API-Key: ${cookieId}" \\
  -H "Content-Type: application/json" \\
  -d '{"job_type": "single-variant", "input_text": "chr17:43094464:G:A"}'`
      },
      {
        title: 'Single SNV Variant (With Options)',
        code: `curl -X POST "${baseUrl}/api/v1/jobs/submit" \\
  -H "X-API-Key: ${cookieId}" \\
  -H "Content-Type: application/json" \\
  -d '{"job_type": "single-variant", "input_text": "chr13_32340300_C_T", "genome": "GRCH38", "gender": "Female"}'`
      },
      {
        title: 'CNV/SV - Deletion',
        code: `curl -X POST "${baseUrl}/api/v1/jobs/submit" \\
  -H "X-API-Key: ${cookieId}" \\
  -H "Content-Type: application/json" \\
  -d '{"job_type": "single-sv-cnv", "input_text": "chr7_55000000_55200000_DEL", "genome": "GRCH38"}'`
      },
      {
        title: 'CNV/SV - Duplication',
        code: `curl -X POST "${baseUrl}/api/v1/jobs/submit" \\
  -H "X-API-Key: ${cookieId}" \\
  -H "Content-Type: application/json" \\
  -d '{"job_type": "single-sv-cnv", "input_text": "chr22_20000000_21000000_DUP", "genome": "GRCH38"}'`
      },
      {
        title: 'CNV/SV - Inversion',
        code: `curl -X POST "${baseUrl}/api/v1/jobs/submit" \\
  -H "X-API-Key: ${cookieId}" \\
  -H "Content-Type: application/json" \\
  -d '{"job_type": "single-sv-cnv", "input_text": "chrX_100000_500000_INV", "genome": "GRCH38"}'`
      }
    ]
  };

  const pollExamples = {
    windows: [
      {
        title: 'Poll Job Status',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/jobs/poll/<job_id>" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }`
      },
      {
        title: 'Get File Names Only',
        code: `(Invoke-RestMethod -Uri "${baseUrl}/api/v1/jobs/poll/<job_id>" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }).files.name`
      }
    ],
    linux: [
      {
        title: 'Poll Job Status',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/jobs/poll/<job_id>"`
      },
      {
        title: 'Get File Names Only (with jq)',
        code: `curl -s -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/jobs/poll/<job_id>" | jq '.files[].name'`
      }
    ]
  };

  const downloadExamples = {
    windows: [
      {
        title: 'Download Text File',
        code: `Invoke-WebRequest -Uri "${baseUrl}/api/v1/jobs/download/<job_id>/BRCA1-clingen.txt" \`
  -Headers @{ "X-API-Key" = "${cookieId}" } \`
  -OutFile "BRCA1-clingen.txt"`
      },
      {
        title: 'Download from Subfolder',
        code: `Invoke-WebRequest -Uri "${baseUrl}/api/v1/jobs/download/<job_id>/ClinVarPLP-Lollipop-BRCA1_files/d3-4.13.0/d3/d3.v4.min.js" \`
  -Headers @{ "X-API-Key" = "${cookieId}" } \`
  -OutFile "d3.v4.min.js"`
      }
    ],
    linux: [
      {
        title: 'Download Text File',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  -o "BRCA1-clingen.txt" \\
  "${baseUrl}/api/v1/jobs/download/<job_id>/BRCA1-clingen.txt"`
      },
      {
        title: 'Download from Subfolder',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  -o "d3.v4.min.js" \\
  "${baseUrl}/api/v1/jobs/download/<job_id>/ClinVarPLP-Lollipop-BRCA1_files/d3-4.13.0/d3/d3.v4.min.js"`
      }
    ]
  };

  const pollResponses = [
    {
      title: 'Queued',
      code: `{
  "job_id": "...",
  "status": "queued",
  "message": null,
  "created_at": "2026-01-12T10:00:00.000Z",
  "started_at": null
}`
    },
    {
      title: 'Running',
      code: `{
  "job_id": "...",
  "status": "running",
  "message": null,
  "created_at": "2026-01-12T10:00:00.000Z",
  "started_at": "2026-01-12T10:00:05.000Z"
}`
    },
    {
      title: 'Failed',
      code: `{
  "job_id": "...",
  "status": "failed",
  "message": "exit code 1",
  "created_at": "...",
  "started_at": "..."
}`
    },
    {
      title: 'Completed',
      code: `{
  "job_id": "...",
  "status": "completed",
  "finished_at": "2026-01-12T10:01:30.000Z",
  "files": [
    { "name": "BRCA1-clingen.txt", "contentType": "text/plain", "data": "..." },
    { "name": "plot.png", "contentType": "image/png", "data": "base64..." },
    { "name": "subdir/file.txt", "contentType": "text/plain", "data": "..." }
  ]
}`
    }
  ];

  const requestBodyFields = [
    { field: 'job_type', type: 'string', required: 'Yes', description: 'gene, single-variant, or single-sv-cnv' },
    { field: 'input_text', type: 'string', required: 'Yes', description: 'Query input (see Input Formats)' },
    { field: 'genome', type: 'string', required: 'No', description: 'GRCH38 (default) or GRCH37' },
    { field: 'gender', type: 'string', required: 'No', description: 'Male, Female, or Unknown (default)' }
  ];

  const inputFormats = [
    { jobType: 'gene', format: 'Gene symbol', examples: 'BRCA1, TP53, EGFR' },
    { jobType: 'single-variant', format: 'chr:pos:ref:alt or chr_pos_ref_alt', examples: 'chr17:43094464:G:A, chr13_32340300_C_T' },
    { jobType: 'single-sv-cnv', format: 'chr_start_end_TYPE', examples: 'chr7_55000000_55200000_DEL, chr22_20000000_21000000_DUP, chrX_100000_500000_INV' }
  ];

  const errorCodes = [
    { code: '400', description: 'Bad request (missing fields, invalid job_type)' },
    { code: '401', description: 'Missing X-API-Key header' },
    { code: '403', description: 'Invalid API key' },
    { code: '404', description: 'Job not found or access denied' },
    { code: '409', description: 'Job not completed (for download)' },
    { code: '429', description: 'Rate limit exceeded (100/hour)' }
  ];

  let copyIdx = 0;

  return (
    <div className="WSNG-api-container">
      <div className="WSNG-api-section">
        <h2>VarXOmics API Documentation</h2>
      </div>

      <div className="WSNG-api-section">
        <h2>Authentication</h2>
        <p className="WSNG-api-description">
          All endpoints require an <code>X-API-Key</code> header with your given API Key:
        </p>
        <pre className="WSNG-api-code WSNG-api-code-inline">X-API-Key: {cookieId}</pre>
        <p className="WSNG-api-note">Rate Limit: 100 requests per hour</p>
      </div>

      <div className="WSNG-api-os-toggle">
        <span className="WSNG-api-os-label">Examples for:</span>
        <div className="WSNG-api-os-buttons">
          <button
            className={`WSNG-api-os-btn ${osType === 'windows' ? 'active' : ''}`}
            onClick={() => setOsType('windows')}
          >
            <i className="fab fa-windows"></i> Windows
          </button>
          <button
            className={`WSNG-api-os-btn ${osType === 'linux' ? 'active' : ''}`}
            onClick={() => setOsType('linux')}
          >
            <i className="fab fa-linux"></i> Linux / macOS
          </button>
        </div>
      </div>

      <div className="WSNG-api-section">
        <h2>Submit Job</h2>
        <p className="WSNG-api-endpoint">
          <span className="WSNG-method post">POST</span>
          <code>/api/v1/jobs/submit</code>
        </p>
        <p className="WSNG-api-description">Submit a non-VCF analysis job.</p>

        <h3>Request Body</h3>
        <table className="WSNG-api-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Required</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {requestBodyFields.map((field, index) => (
              <tr key={index}>
                <td><code>{field.field}</code></td>
                <td>{field.type}</td>
                <td>{field.required}</td>
                <td>{field.description}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3>Examples</h3>
        <div className="WSNG-api-examples">
          {submitExamples[osType].map((example, index) => {
            const idx = copyIdx++;
            return (
              <div key={index} className="WSNG-api-example">
                <div className="WSNG-api-example-header">
                  <h4>{example.title}</h4>
                  <button
                    className={`WSNG-copy-btn ${copiedIndex === idx ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(example.code, idx)}
                  >
                    <i className={`fas ${copiedIndex === idx ? 'fa-check' : 'fa-copy'}`}></i>
                    {copiedIndex === idx ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="WSNG-api-code">{example.code}</pre>
              </div>
            );
          })}
        </div>

        <h3>Response</h3>
        <pre className="WSNG-api-code">{`{ "job_id": "6a5cf6a2-e6b9-4598-ae7a-b75fb5eb3599", "status": "queued" }`}</pre>
      </div>

      <div className="WSNG-api-section">
        <h2>Poll Job</h2>
        <p className="WSNG-api-endpoint">
          <span className="WSNG-method get">GET</span>
          <code>/api/v1/jobs/poll/:job_id</code>
        </p>
        <p className="WSNG-api-description">Check job status. Returns results when completed.</p>

        <h3>Examples</h3>
        <div className="WSNG-api-examples">
          {pollExamples[osType].map((example, index) => {
            const idx = copyIdx++;
            return (
              <div key={index} className="WSNG-api-example">
                <div className="WSNG-api-example-header">
                  <h4>{example.title}</h4>
                  <button
                    className={`WSNG-copy-btn ${copiedIndex === idx ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(example.code, idx)}
                  >
                    <i className={`fas ${copiedIndex === idx ? 'fa-check' : 'fa-copy'}`}></i>
                    {copiedIndex === idx ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="WSNG-api-code">{example.code}</pre>
              </div>
            );
          })}
        </div>

        <h3>Responses</h3>
        <div className="WSNG-api-responses">
          {pollResponses.map((response, index) => (
            <div key={index} className="WSNG-api-response">
              <h4>{response.title}</h4>
              <pre className="WSNG-api-code">{response.code}</pre>
            </div>
          ))}
        </div>
      </div>

      <div className="WSNG-api-section">
        <h2>Download File</h2>
        <p className="WSNG-api-endpoint">
          <span className="WSNG-method get">GET</span>
          <code>/api/v1/jobs/download/:job_id/:filename</code>
        </p>
        <p className="WSNG-api-description">Download a specific output file. Supports nested paths.</p>

        <h3>Examples</h3>
        <div className="WSNG-api-examples">
          {downloadExamples[osType].map((example, index) => {
            const idx = copyIdx++;
            return (
              <div key={index} className="WSNG-api-example">
                <div className="WSNG-api-example-header">
                  <h4>{example.title}</h4>
                  <button
                    className={`WSNG-copy-btn ${copiedIndex === idx ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(example.code, idx)}
                  >
                    <i className={`fas ${copiedIndex === idx ? 'fa-check' : 'fa-copy'}`}></i>
                    {copiedIndex === idx ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="WSNG-api-code">{example.code}</pre>
              </div>
            );
          })}
        </div>
      </div>

      <div className="WSNG-api-section">
        <h2>Input Formats</h2>
        <table className="WSNG-api-table">
          <thead>
            <tr>
              <th>Job Type</th>
              <th>Format</th>
              <th>Examples</th>
            </tr>
          </thead>
          <tbody>
            {inputFormats.map((format, index) => (
              <tr key={index}>
                <td><code>{format.jobType}</code></td>
                <td>{format.format}</td>
                <td><code>{format.examples}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="WSNG-api-note">SV/CNV Types: DEL, DUP, INS, INV, CNV</p>
      </div>

      <div className="WSNG-api-section">
        <h2>Error Codes</h2>
        <table className="WSNG-api-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {errorCodes.map((error, index) => (
              <tr key={index}>
                <td><span className={`WSNG-status-code code-${error.code}`}>{error.code}</span></td>
                <td>{error.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Api;
